import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { analyticsAPI, seedDemo } from '../lib/api';
import { Button } from '../components/ui/button';
import UpgradeBanner from '../components/UpgradeBanner';
import {
  PhoneCall, PhoneIncoming, Clock, TrendingUp, Bot, Megaphone, BookOpen,
  ArrowUpRight, Zap, Loader2
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useNavigate } from 'react-router-dom';

const MetricCard = ({ icon: Icon, label, value, change, color }) => (
  <div className="glass-card rounded-xl p-5 animate-fade-in" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      {change && (
        <span className="text-xs text-emerald-400 flex items-center gap-0.5">
          <ArrowUpRight className="w-3 h-3" /> {change}
        </span>
      )}
    </div>
    <div className="text-2xl font-bold font-['Outfit'] text-white">{value}</div>
    <div className="text-sm text-slate-500 mt-1">{label}</div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-white/10 rounded-lg p-3 shadow-xl text-sm">
      <p className="text-slate-400 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }} className="font-medium">
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ovRes, chRes] = await Promise.all([analyticsAPI.overview(), analyticsAPI.charts()]);
      setOverview(ovRes.data);
      setCharts(chRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDemo = async () => {
    setSeeding(true);
    try {
      await seedDemo();
      await loadData();
    } catch (err) {
      console.error(err);
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl" data-testid="dashboard-overview">
      {/* Upgrade Banner for Standard Plan Users */}
      <UpgradeBanner />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white">
            Welcome back, {user?.name?.split(' ')[0] || 'there'}
          </h1>
          <p className="text-slate-400 mt-1">Here's what's happening with your AI agents</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={handleSeedDemo}
            disabled={seeding}
            variant="outline"
            data-testid="seed-demo-button"
            className="border-white/10 text-slate-300 hover:bg-white/5 hover:text-white"
          >
            {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
            Seed Demo Data
          </Button>
          <Button
            onClick={() => navigate('/dashboard/agents')}
            data-testid="new-agent-shortcut"
            className="bg-amber-600 hover:bg-amber-700 text-white shadow-[0_0_20px_rgba(245,158,11,0.3)]"
          >
            <Bot className="w-4 h-4 mr-2" /> New Agent
          </Button>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={PhoneCall} label="Total Calls" value={overview?.total_calls || 0} change="+12%" color="bg-amber-600/20 text-amber-400" />
        <MetricCard icon={PhoneIncoming} label="Answered" value={overview?.answered_calls || 0} change="+8%" color="bg-emerald-600/20 text-emerald-400" />
        <MetricCard icon={Clock} label="Avg Duration" value={`${Math.floor((overview?.avg_duration || 0) / 60)}m ${(overview?.avg_duration || 0) % 60}s`} color="bg-cyan-600/20 text-cyan-400" />
        <MetricCard icon={TrendingUp} label="Conversion" value={`${overview?.conversion_rate || 0}%`} change="+5%" color="bg-amber-600/20 text-amber-400" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-card rounded-xl p-6" data-testid="calls-chart">
          <h3 className="text-lg font-semibold font-['Outfit'] text-white mb-4">Call Volume</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={charts?.daily || []}>
                <defs>
                  <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" stroke="#475569" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#475569" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="calls" stroke="#F59E0B" fill="url(#colorCalls)" strokeWidth={2} name="Calls" />
                <Area type="monotone" dataKey="answered" stroke="#06B6D4" fill="transparent" strokeWidth={2} strokeDasharray="5 5" name="Answered" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass-card rounded-xl p-6" data-testid="conversions-chart">
          <h3 className="text-lg font-semibold font-['Outfit'] text-white mb-4">Conversions</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts?.daily || []}>
                <XAxis dataKey="date" stroke="#475569" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#475569" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="conversions" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Conversions" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="glass-card rounded-xl p-5 cursor-pointer hover:-translate-y-1 transition-transform" onClick={() => navigate('/dashboard/agents')} data-testid="quick-agents">
          <div className="flex items-center gap-3">
            <Bot className="w-5 h-5 text-amber-400" />
            <span className="text-white font-medium">AI Agents</span>
          </div>
          <div className="text-3xl font-bold font-['Outfit'] text-white mt-3">{overview?.total_agents || 0}</div>
          <div className="text-sm text-slate-500 mt-1">Active agents</div>
        </div>
        <div className="glass-card rounded-xl p-5 cursor-pointer hover:-translate-y-1 transition-transform" onClick={() => navigate('/dashboard/campaigns')} data-testid="quick-campaigns">
          <div className="flex items-center gap-3">
            <Megaphone className="w-5 h-5 text-cyan-400" />
            <span className="text-white font-medium">Campaigns</span>
          </div>
          <div className="text-3xl font-bold font-['Outfit'] text-white mt-3">{overview?.total_campaigns || 0}</div>
          <div className="text-sm text-slate-500 mt-1">Total campaigns</div>
        </div>
        <div className="glass-card rounded-xl p-5 cursor-pointer hover:-translate-y-1 transition-transform" onClick={() => navigate('/dashboard/knowledge-base')} data-testid="quick-kb">
          <div className="flex items-center gap-3">
            <BookOpen className="w-5 h-5 text-emerald-400" />
            <span className="text-white font-medium">Knowledge Base</span>
          </div>
          <div className="text-3xl font-bold font-['Outfit'] text-white mt-3">{overview?.total_kb_docs || 0}</div>
          <div className="text-sm text-slate-500 mt-1">Documents indexed</div>
        </div>
      </div>
    </div>
  );
}
